import React from 'react';
import { HStack, ContextMenu, ContextMenuContent, ContextMenuTrigger } from '@cad-arc';
import { PillUnit } from '@mark43/cad-api';
import { observer } from 'mobx-react-lite';
import { ContextMenuWriteContextValue } from '../../queues/context/context-menu-state-context';
import { getVisibleNumberOfItems } from '../../queues/utils/get-visible-number-of-items';
import { MultiUnitContextMenu } from '../../unit-context-menu/multi-unit-context-menu';
import { MoreUnits } from './more-units';
import { UnitPill } from './unit-pill';

const CHAR_WIDTH = 13;
function getApproximateStringWidth(str: string): number {
    return str.length * CHAR_WIDTH;
}

function getNumToDisplay(containerWidth: number, units: PillUnit[]): number {
    return getVisibleNumberOfItems(containerWidth, units, (unit) =>
        getApproximateStringWidth(unit.callSign)
    );
}
const MINIMUM_NUM_UNITS_TO_DISPLAY = 2;

export const UnitsDisplay = observer(function UnitsDisplay({
    units,
    width,
    minNumberToDisplay,
    enableContextMenu,
    contextMenuStateWrite,
    wrap,
    ghostUnitIds,
}: {
    units: PillUnit[];
    width?: number;
    minNumberToDisplay?: number;
    enableContextMenu?: boolean;
    contextMenuStateWrite?: ContextMenuWriteContextValue;
    wrap?: boolean;
    ghostUnitIds?: number[];
}): React.ReactElement {
    const numToDisplay = width
        ? Math.max(
              minNumberToDisplay ?? MINIMUM_NUM_UNITS_TO_DISPLAY,
              getNumToDisplay(width, units)
          )
        : minNumberToDisplay ?? units.length;
    const unitsToDisplay = units.slice(0, numToDisplay);
    const unitsBehindTip = units.slice(numToDisplay);
    const unitsBehindTipEl =
        unitsBehindTip.length > 0 ? (
            <MoreUnits units={unitsBehindTip} ghostUnitIds={ghostUnitIds} />
        ) : undefined;

    const wrappedContextMenuStateWrite: React.MouseEventHandler<HTMLDivElement> = React.useCallback(() => {
        if (contextMenuStateWrite) {
            contextMenuStateWrite({ kind: 'MULTI_UNIT', data: unitsBehindTip });
        }
        return undefined;
    }, [contextMenuStateWrite, unitsBehindTip]);

    return (
        <HStack justifyContent="flex-start" gap={1} spacing={0} wrap={wrap ? 'wrap' : undefined}>
            {unitsToDisplay.map((unit) => (
                <UnitPill
                    enableContextMenu={enableContextMenu}
                    contextMenuStateWrite={
                        enableContextMenu && contextMenuStateWrite
                            ? contextMenuStateWrite
                            : undefined
                    }
                    key={unit.callSign}
                    unit={unit}
                    variant={ghostUnitIds?.includes(unit.id) ? 'ghost' : 'default'}
                />
            ))}
            {enableContextMenu && contextMenuStateWrite && unitsBehindTipEl ? (
                <div onContextMenu={wrappedContextMenuStateWrite}>{unitsBehindTipEl}</div>
            ) : enableContextMenu ? (
                <ContextMenu>
                    <ContextMenuTrigger>{unitsBehindTipEl}</ContextMenuTrigger>
                    <ContextMenuContent>
                        <MultiUnitContextMenu data={unitsBehindTip} />
                    </ContextMenuContent>
                </ContextMenu>
            ) : (
                unitsBehindTipEl
            )}
        </HStack>
    );
});
